import { orderBy } from 'lodash-es';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';

import { OrderService } from 'src/order/service/order.service';
import { Status } from 'src/shared/ui/components';
import { Button, Table, CheckboxField } from 'src/shared/ui/elements';
import { LinkButton } from 'src/shared/ui/elements/index';
import { currency } from 'src/shared/utils/currency';

const OrderItemDesktop = ({ $item: order }) => {
  const status = order.status;
  const card = order.paymentMethod;
  return (
    <tr>
      <td>
        <Status className="mx-auto" $status={status} />
      </td>
      <td>
        <p className="fw-500">
          <Link to={`/orders/${order.orderHash}`}>{order.id}</Link>
        </p>
        <p className="fc-gray-55 fs-12 mt-4">
          {DateTime.cast(order?.billDate)?.toLocaleString(DateTime.DATE_SHORT)}
        </p>
        <p className="fw-600 fs-13 mt-2">{OrderService.statusLabel(status)}</p>
      </td>
      <td>{order.account?.fullName || card?.nameOnCard || '-'}</td>
      <td className="fw-500">{currency(order.price)}</td>
      <td className="fw-500">{currency(order.paid)}</td>
      <td className="fc-accent-50 fw-600">{currency(order.balance)}</td>
      <td>
        {card && (
          <>
            <p className="fw-600 fs-14">Credit card</p>
            <p className="fs-12 fw-500 fc-gray-50 mt-4">{card?.label}</p>
          </>
        )}

        {!card && order.balance > 0 && (
          <LinkButton
            className="alt outline inline-block"
            to={`/orders/${order.orderHash}`}
          >
            Pay balance
          </LinkButton>
        )}
      </td>
    </tr>
  );
};

const OrderItemMobile = ({ $item: order }) => {
  const history = useHistory();
  return (
    <li className="pa-16 shadow-2">
      <p>{DateTime.cast(order.billDate).toLocaleString(DateTime.DATE_SHORT)}</p>

      <div className="my-16">
        {order.orderItems.map((item, index) => (
          <div key={`${order.id}-${index}-mobile`} className="mb-4 mc-b">
            <div className="fw-500">{item.event?.label || ''}</div>
            <div className="fs-12 fc-gray-55 mt-4">
              ({item.participant || 'No participants'})
            </div>
          </div>
        ))}
      </div>

      <p>
        <b>Order Total:</b> {currency(order.price)}
      </p>
      <p className="mt-4">
        <b>Amount Paid</b> {currency(order.paid)}
      </p>

      <div className="bg-error-95 fc-error-30 pa-12 mt-12 br-4 text-center">
        Balance: {currency(order.balance)}
      </div>

      {order.balance > 0 && (
        <Button
          className="alt mt-8 fill-width"
          onClick={() => {
            history.push(`/orders/${order.orderHash}`, '_blank');
          }}
        >
          Pay now
        </Button>
      )}
    </li>
  );
};

export const OrderHistory = ({ $orders }) => {
  const orders = React.useMemo(
    () => $orders.map(OrderService.getRegistrationData),
    [$orders],
  );

  const { activeOrders, paidOrders } = useMemo(() => {
    const openOrders = orders.filter((order) => order.balance > 0);
    const paidOrders = orders.filter((order) => order.balance === 0);
    const {
      activeOrders: activeOrdersToShow,
      // pendingOrders: pendingOrdersToShow,
    } = openOrders.reduce(
      (accumulator, order) => {
        if (!order?.id) {
          return accumulator;
        }
        if (order.isOrderDue) {
          accumulator.activeOrders.push(order);
        } else {
          // accumulator.pendingOrders.push(order);
        }
        return accumulator;
      },
      { activeOrders: [], pendingOrders: [] },
    );
    return {
      activeOrders: activeOrdersToShow,
      paidOrders,
      // pendingOrders: pendingOrdersToShow,
    };
  }, [orders]);
  const orderTypes = [
    {
      count: activeOrders.length,
      label: 'Due',
      value: 'active',
    },
    {
      count: paidOrders.length,
      label: 'Paid',
      value: 'paid',
    },
    // {
    //   count: pendingOrders.length,
    //   label: 'Upcoming',
    //   value: 'pending',
    // },
  ];

  const availableOrderTypes = orderTypes.filter((type) => type.count > 0);

  const [selectedOrderTypes, setSelectedOrderTypes] = useState(
    availableOrderTypes.map((type) => type.value),
  );
  const filteredOrders = React.useMemo(() => {
    const filteredOrders = selectedOrderTypes.reduce((accumulator, type) => {
      if (type === 'active') {
        accumulator.push(...activeOrders);
      } else if (type === 'paid') {
        accumulator.push(...paidOrders);
      } else if (type === 'pending') {
        // accumulator.push(...pendingOrders);
      }
      return accumulator;
    }, []);
    return orderBy(filteredOrders, ['billDate'], ['asc']);
  }, [
    activeOrders,
    paidOrders,
    // pendingOrders,
    selectedOrderTypes,
  ]);

  return (
    <div>
      <div className="flex tw-flex-col tw-gap-4 tw-mb-4 tw-mt-6">
        <h2>
          Order History ({(activeOrders.length || 0) + (paidOrders.length || 0)}
          )
        </h2>
        <div className="flex tw-gap-3">
          {[
            { type: 'paid', orders: paidOrders },
            { type: 'active', orders: activeOrders },
            // { type: 'pending', orders: pendingOrders },
          ].map(
            ({ type, orders }) =>
              orders.length > 0 && (
                <CheckboxField
                  key={type}
                  name={type}
                  $label={`${type.charAt(0).toUpperCase() + type.slice(1)} Order${orders.length > 1 ? 's' : ''} (${orders.length})`}
                  labelClassName="fs-18 fw-500"
                  onChange={(e) => {
                    const isChecked = e.target?.checked;
                    setSelectedOrderTypes((prev) =>
                      isChecked
                        ? [...new Set([...prev, type])]
                        : prev.filter((t) => t !== type),
                    );
                  }}
                  checked={selectedOrderTypes.includes(type)}
                />
              ),
          )}
        </div>
      </div>
      <Table
        className="mt-8"
        $headers={[
          '',
          'Order',
          'Customer',
          'Total',
          'Paid',
          'Balance',
          'Payment',
        ]}
        $items={filteredOrders}
        $desktopItem={OrderItemDesktop}
        $mobileItem={OrderItemMobile}
      />
    </div>
  );
};
